/**
 * ContactSessionManager — owns the registry of ContactRecords, wires adapter
 * events into the state machine, and emits commands via ContactEffects.
 */

import type { ConnectContact } from '@/types';
import type { IConnectAdapter, Unsubscribe } from './adapter/types';
import type { ContactRecord, ContactEvent, ContactsSnapshot } from './types';
import { TERMINAL_STAGES } from './types';
import { classifyContact, KIND_POLICY } from './kindPolicy';
import { transition } from './contactMachine';
import type { ContactEffects } from './effects';

// ---------------------------------------------------------------------------
// Helpers the manager needs from the outside world
// ---------------------------------------------------------------------------

export interface SessionHelpers {
  /** Is this contact in the missed state? (wraps contactUtils.isContactInMissedState) */
  isInMissedState(contact: ConnectContact): boolean;
  /** Is this contact in after-call-work state? */
  isInAfterCallWork(contact: ConnectContact): boolean;
  /** Does this contact have an active call leg? */
  hasActiveCallLeg(contact: ConnectContact): boolean;
}

// ---------------------------------------------------------------------------
// Manager
// ---------------------------------------------------------------------------

export class ContactSessionManager {
  private registry = new Map<string, ContactRecord>();
  private adapter: IConnectAdapter;
  private effects: ContactEffects;
  private helpers: SessionHelpers;
  private unsubscribes: Unsubscribe[] = [];
  /** Per-contact event unsubscribers, keyed by contactId. */
  private contactUnsubs = new Map<string, Unsubscribe[]>();

  constructor(
    adapter: IConnectAdapter,
    effects: ContactEffects,
    helpers: SessionHelpers
  ) {
    this.adapter = adapter;
    this.effects = effects;
    this.helpers = helpers;
  }

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  /** Subscribe to adapter events. Call once. Returns detach function. */
  attach(): () => void {
    const unsubContact = this.adapter.onContact(contact => {
      this.registerContact(contact as ConnectContact);
    });
    this.unsubscribes.push(unsubContact);
    return () => this.detach();
  }

  /** Unsubscribe all adapter + per-contact event handlers. */
  detach(): void {
    this.unsubscribes.forEach(fn => {
      try {
        fn();
      } catch {
        /* ok */
      }
    });
    this.unsubscribes = [];
    this.unsubscribeAllContacts();
    this.registry.clear();
  }

  /** Reset all state — used when isReady becomes false. */
  reset(): void {
    this.detach();
  }

  // -----------------------------------------------------------------------
  // Seed from agent.getContacts() (page refresh / reconnection)
  // -----------------------------------------------------------------------

  seed(contacts: ConnectContact[]): void {
    let hadMissed = false;
    const enums = this.adapter.getCcpEnums();

    for (const c of contacts) {
      // Missed-state contacts → clear them, track for NOT_ROUTABLE.
      // Outbound calls are agent-initiated, so the agent never "misses" them.
      if (c.isInbound() && this.helpers.isInMissedState(c)) {
        hadMissed = true;
        try {
          c.clear({ success: () => {}, failure: () => {} });
        } catch {
          /* ok */
        }
        continue;
      }

      // Stale chat ACW → auto-clear and set agent routable
      if (this.helpers.isInAfterCallWork(c) && this.adapter.isChatContact(c)) {
        try {
          c.clear({ success: () => {}, failure: () => {} });
        } catch {
          /* ok */
        }
        this.adapter.setAgentState('ROUTABLE');
        continue;
      }

      // Active ACW (non-chat) → register so the snapshot can expose it
      if (this.helpers.isInAfterCallWork(c)) {
        this.registerContact(c);
        continue;
      }

      // Only register contacts with active call legs
      if (!this.helpers.hasActiveCallLeg(c)) continue;

      // Register and check if it should be incoming
      this.registerContact(c);

      const stateType = c.getState()?.type;
      const isIncoming =
        c.isInbound() &&
        !this.adapter.isMonitorContact(c) &&
        !c.isConnected() &&
        (stateType === enums.ContactStateType.INCOMING ||
          stateType === enums.ContactStateType.CONNECTING ||
          stateType === enums.ContactStateType.PENDING);

      if (isIncoming) {
        this.effects.setIncoming(c);
      }
    }

    if (hadMissed) {
      this.effects.setAgentNotRoutable();
    }
  }

  // -----------------------------------------------------------------------
  // Snapshot
  // -----------------------------------------------------------------------

  getSnapshot(): ContactsSnapshot {
    const active: ContactRecord[] = [];
    let incomingId: string | null = null;
    const pendingFinishIds: string[] = [];

    for (const rec of this.registry.values()) {
      const isTerminal = TERMINAL_STAGES.has(rec.stage);

      // Pending "Finish" comes from two sources:
      // 1. `afterCallWork` stage — explicit ACW event
      // 2. `ended` stage — voice/callback keep their session for post-call docs,
      //    and chat ends route to ACW. Monitor contacts are excluded.
      if (
        rec.stage === 'afterCallWork' ||
        (rec.stage === 'ended' && rec.kind !== 'monitor')
      ) {
        pendingFinishIds.push(rec.id);
      }

      if (isTerminal) continue;

      active.push(rec);
      // For callback contacts, the modal stays visible during 'accepted'
      // (keepModalOnAccept) so the agent sees the "dialing customer" timer.
      if (
        rec.stage === 'incoming' ||
        (rec.stage === 'accepted' && KIND_POLICY[rec.kind].keepModalOnAccept)
      ) {
        incomingId = rec.id;
      }
    }

    return { active, incomingId, pendingFinishIds };
  }

  /** Look up a contact record by ID. */
  getRecord(id: string): ContactRecord | undefined {
    return this.registry.get(id);
  }

  /**
   * Remove a finished contact record from the registry and tear down its
   * event subscriptions. Used by "Finish" so the pending list drops the contact
   * even if contact.clear() did not emit onDestroy (e.g. voice already ended).
   */
  dismissContact(contactId: string): void {
    this.registry.delete(contactId);
    const subs = this.contactUnsubs.get(contactId);
    if (subs) {
      for (const fn of subs) {
        try {
          fn();
        } catch {
          /* ok */
        }
      }
      this.contactUnsubs.delete(contactId);
    }
  }

  /** Look up a contact record by call-scope id (see adapter.getCallScopeContactId). */
  private findRecordByCallScope(
    callScopeId: string
  ): ContactRecord | undefined {
    for (const rec of this.registry.values()) {
      if (rec.callScopeId === callScopeId) return rec;
    }
    return undefined;
  }

  /**
   * Outbound calls have no Boost metadata, so every /call request for them
   * carries isOutbound=true. Same derivation as the contact-center components:
   * Boolean(activeContact && !activeContact.isInbound()) — `inbound` was
   * captured from contact.isInbound() at registration.  Callback contacts are
   * excluded: Connect reports them as outbound (isInbound() === false), but
   * the backend must resolve their case / workspace like an inbound call.
   */
  private isOutboundCall(rec: ContactRecord | undefined): boolean {
    return Boolean(rec && !rec.inbound && rec.kind !== 'callback');
  }

  // -----------------------------------------------------------------------
  // Registration
  // -----------------------------------------------------------------------

  private registerContact(contact: ConnectContact): void {
    let contactId: string;
    let callScopeId: string;
    try {
      contactId = contact.getContactId();
      callScopeId = this.adapter.getCallScopeContactId(contact);
    } catch {
      return; // contact destroyed
    }

    // Already registered — could be attribute update OR contact re-creation
    // after Connect error (same contactId, new object reference).
    if (this.registry.has(contactId)) {
      const existing = this.registry.get(contactId)!;
      const isNewObject = existing.contact !== contact;

      if (isNewObject) {
        // New contact object (post-error re-creation or re-delivery).
        // Update the reference so future events fire on the new object.
        existing.contact = contact;
        this.subscribeContactEvents(contact, contactId, callScopeId);

        if (TERMINAL_STAGES.has(existing.stage)) {
          // Terminal stage → reset to pending so the new contact can go
          // through the full lifecycle.
          console.log(
            '[AC-Contacts] re-delivery: NEW object from terminal — resetting',
            {
              contactId,
              oldStage: existing.stage,
            }
          );
          existing.stage = 'pending';
          existing.streamInitiated = false;
          this.checkCurrentState(contact, contactId);
          return;
        }

        console.log(
          '[AC-Contacts] re-delivery: NEW object, non-terminal — metadata only',
          {
            contactId,
            stage: existing.stage,
          }
        );
        this.effects.fetchMetadata(callScopeId, this.isOutboundCall(existing));
        return;
      }

      // Same object — attribute update only.
      console.log('[AC-Contacts] re-delivery: same object — metadata only', {
        contactId,
        stage: existing.stage,
      });
      this.effects.fetchMetadata(callScopeId, this.isOutboundCall(existing));
      return;
    }

    // Classify once.
    const isMonitor = this.adapter.isMonitorContact(contact);
    const kind = classifyContact(contact, isMonitor);
    const inbound = contact.isInbound();
    const dialpadOutbound = this.adapter.isDialContact(contact);

    console.log(
      '[AC-Contacts] registerContact',
      JSON.stringify({
        contactId,
        callScopeId,
        kind,
        inbound,
        dialpadOutbound,
        isMonitor,
      })
    );

    const record: ContactRecord = {
      contact,
      id: contactId,
      callScopeId,
      kind,
      inbound,
      dialpadOutbound,
      stage: 'pending',
      streamInitiated: false,
      callbackAccepted: false,
      createdAt: Date.now(),
    };

    // Run the initial 'delivered' event to classify from current state.
    const afterDelivered = transition(record, { type: 'delivered' });
    this.registry.set(contactId, afterDelivered.record);

    // Apply commands from the delivered transition (e.g. auto-accept).
    this.applyCommands(afterDelivered.commands);

    // Check current state to see if we missed the incoming window.
    this.checkCurrentState(contact, contactId);

    // Subscribe to per-contact lifecycle events.
    this.subscribeContactEvents(contact, contactId, callScopeId);
  }

  /** If the contact is already in an incoming-like state when registered,
   *  fire state_incoming so the modal appears. */
  private checkCurrentState(contact: ConnectContact, contactId: string): void {
    try {
      const enums = this.adapter.getCcpEnums();
      const stateType = contact.getState()?.type;
      const isIncomingState =
        contact.isInbound() &&
        !this.adapter.isMonitorContact(contact) &&
        !contact.isConnected() &&
        (stateType === enums.ContactStateType.INCOMING ||
          stateType === enums.ContactStateType.CONNECTING ||
          stateType === enums.ContactStateType.PENDING);

      if (isIncomingState) {
        this.handleEvent(contactId, { type: 'state_incoming' });
      }

      // Also check missed / ACW on registration.
      if (this.helpers.isInMissedState(contact)) {
        this.handleEvent(contactId, { type: 'missed' });
      }
      if (this.helpers.isInAfterCallWork(contact)) {
        this.handleEvent(contactId, { type: 'acw' });
      }
    } catch {
      // contact may have been destroyed
    }
  }

  // -----------------------------------------------------------------------
  // Per-contact Streams event subscriptions
  // -----------------------------------------------------------------------

  private subscribeContactEvents(
    contact: ConnectContact,
    contactId: string,
    _callScopeId: string
  ): void {
    const subs: Unsubscribe[] = [];

    const onIncomingLike = () => {
      this.handleEvent(contactId, { type: 'state_incoming' });
    };

    try {
      contact.onIncoming(() => onIncomingLike());
    } catch {
      /* ok */
    }
    try {
      contact.onConnecting(() => onIncomingLike());
    } catch {
      /* ok */
    }
    try {
      contact.onPending(() => onIncomingLike());
    } catch {
      /* ok */
    }

    try {
      contact.onAccepted(() => {
        this.handleEvent(contactId, { type: 'accepted' });
      });
    } catch {
      /* ok */
    }

    try {
      contact.onConnected(() => {
        this.handleEvent(contactId, { type: 'connected' });
      });
    } catch {
      /* ok */
    }

    try {
      contact.onACW?.(() => {
        this.handleEvent(contactId, { type: 'acw' });
      });
    } catch {
      /* ok */
    }

    try {
      contact.onEnded(() => {
        const rec = this.registry.get(contactId);
        const inAcw = rec ? this.helpers.isInAfterCallWork(contact) : false;
        this.handleEvent(contactId, { type: 'ended', inAcw });
      });
    } catch {
      /* ok */
    }

    try {
      contact.onDestroy(() => {
        this.handleEvent(contactId, { type: 'destroyed' });
      });
    } catch {
      /* ok */
    }

    try {
      contact.onMissed(() => {
        this.handleEvent(contactId, { type: 'missed' });
      });
    } catch {
      /* ok */
    }

    try {
      contact.onError(() => {
        console.log(
          '[AC-Contacts] onError — customer decline / ringtone stop',
          contactId
        );
        this.handleEvent(contactId, { type: 'error' });
      });
    } catch {
      /* ok */
    }

    this.contactUnsubs.set(contactId, subs);
  }

  private unsubscribeAllContacts(): void {
    for (const subs of this.contactUnsubs.values()) {
      subs.forEach(fn => {
        try {
          fn();
        } catch {
          /* ok */
        }
      });
    }
    this.contactUnsubs.clear();
  }

  // -----------------------------------------------------------------------
  // Event → transition → commands
  // -----------------------------------------------------------------------

  private handleEvent(contactId: string, event: ContactEvent): void {
    const record = this.registry.get(contactId);
    if (!record) return;

    // Terminal stages reject everything (except destroy, which the machine handles).
    if (TERMINAL_STAGES.has(record.stage) && event.type !== 'destroyed') {
      console.log('[AC-Contacts] handleEvent BLOCKED (terminal stage)', {
        contactId,
        stage: record.stage,
        eventType: event.type,
      });
      return;
    }

    console.log(
      '[AC-Contacts] handleEvent',
      JSON.stringify({
        contactId,
        eventType: event.type,
        prevStage: record.stage,
      })
    );

    const { record: next, commands } = transition(record, event);

    console.log(
      '[AC-Contacts] transition',
      JSON.stringify({
        contactId,
        nextStage: next.stage,
        commands: commands.map(c => c.op),
      })
    );

    this.registry.set(contactId, next);
    this.applyCommands(commands);
  }

  // -----------------------------------------------------------------------
  // Command execution
  // -----------------------------------------------------------------------

  private applyCommands(
    commands: Array<{ op: string; [key: string]: unknown }>
  ): void {
    for (const cmd of commands) {
      switch (cmd.op) {
        case 'setIncoming': {
          const rec = this.registry.get(cmd.id as string);
          if (rec) this.effects.setIncoming(rec.contact);
          break;
        }
        case 'clearIncoming':
          this.effects.clearIncoming();
          break;
        case 'clearContactInformation':
          this.effects.clearContactInformation();
          break;
        case 'fetchMetadata': {
          const rec = this.findRecordByCallScope(cmd.callScopeId as string);
          this.effects.fetchMetadata(
            cmd.callScopeId as string,
            this.isOutboundCall(rec)
          );
          break;
        }
        case 'registerSession':
          this.effects.registerSession(
            cmd.callScopeId as string,
            cmd.sessionType as 'voice' | 'chat',
            cmd.startedAt as number | undefined,
            cmd.focus as boolean | undefined
          );
          break;
        case 'initiateStream': {
          const rec = this.findRecordByCallScope(cmd.callScopeId as string);
          this.effects.initiateStream(
            cmd.callScopeId as string,
            this.isOutboundCall(rec)
          );
          break;
        }
        case 'setAfterCallWork': {
          const rec = this.registry.get(cmd.id as string);
          if (rec) this.effects.setAfterCallWork(rec.contact);
          break;
        }
        case 'removeFromActive': {
          const rec = this.registry.get(cmd.id as string);
          if (rec) this.effects.removeFromActive(rec.contact);
          break;
        }
        case 'setAgentNotRoutable':
          this.effects.setAgentNotRoutable();
          break;
        case 'clearConnectContact': {
          const rec = this.registry.get(cmd.id as string);
          if (rec) this.effects.clearConnectContact(rec.contact);
          break;
        }
        case 'autoAcceptContact': {
          const rec = this.registry.get(cmd.id as string);
          if (rec) this.effects.autoAcceptContact(rec.contact);
          break;
        }
        case 'handleChatAccepted': {
          const rec = this.registry.get(cmd.id as string);
          if (rec) this.effects.handleChatAccepted(rec.contact);
          break;
        }
        case 'clearContactStream':
          this.effects.clearStream(cmd.callScopeId as string);
          break;
      }
    }
  }
}
